/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { connect, useDispatch } from 'react-redux';
import JSFileDownload from 'js-file-download';
import { FormattedMessage, useIntl } from 'react-intl';

import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import { Typography, Card, CardMedia, CardActions } from '@material-ui/core';

import TextButton from '../../../elements/TextButton';
import { generateSdk } from '../../../data/operations';
import { ApplicationState, ApiReducer, SdkReducer, UserReducer } from '../../../data/interfaces';
import ApiInfoBar from '../../shared/ApiInfoBar';
import { useStyles } from './styles';
import { setErrorNotification } from '../../../store/actions/notification'
import { PreLoaderLogo } from '../../../elements/PreLoaderLogo';
import {trackApiSDKDownload} from "../../../util/app-insights";

export interface SdkProps {
    apiInfo: ApiReducer,
    sdkInfo: SdkReducer,
    userReducer: UserReducer,
}

function ApiSdks({ apiInfo, sdkInfo, userReducer }: SdkProps) {
    const intl = useIntl();
    const classes = useStyles();
    const { api } = apiInfo;
    const { loading, languages: sdkLanguages } = sdkInfo;
    const { orgs: userOrganizations, selectedOrg } = userReducer;
    const dispatch = useDispatch();

    async function downloadSDK(language: string) {
        try {
            if (api.id && selectedOrg.uuid) {
                const response = await generateSdk(api.id, selectedOrg.uuid, language);
                const sdkZipName = response?.headers['content-disposition'].match(/filename="(.+)"/)[1];
                const sdkZip = response?.data;
                JSFileDownload(sdkZip, sdkZipName);
                trackApiSDKDownload(`${api.id}`, `${sdkZipName}`);
            }
        } catch (error) {
            dispatch(setErrorNotification('ERROR: ' + error.response.data.description ||
                error.response.data.error_description))
        }
    }

    const sdkCards = sdkLanguages.map((language, index) => {
        return (
            <Grid key={index} item>
                <div style={{ minWidth: 200, textAlign: 'center', margin: '10px' }}>
                    <Card className={classes.cardRoot}>
                        <Typography className={classes.cardTitle}>
                            {language}
                        </Typography>
                        <CardMedia
                            component="img"
                            title={language}
                            className={classes.cardMedia}
                            height="150"
                            image={'../../../themes/default/images/' + language + '.svg'}
                        />
                        <CardActions >
                            <Grid container justifyContent='center'>
                                <TextButton
                                    testId={"sdk-" + language + "-button"}
                                    buttonLabel={intl.formatMessage({
                                        id: "api.apiSdks.buttons.downloadSdk",
                                        defaultMessage: "Download"
                                    })}
                                    onClick={() => downloadSDK(language)}
                                />
                            </Grid>
                        </CardActions>
                    </Card>
                </div>
            </Grid>
        )
    })
    return (
        <main className={classes.content}>
            <ApiInfoBar />
            <Container className={classes.container} classes={{ root: classes.componentPadding }} >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8} lg={9}>
                        <Typography className={classes.title}>
                            <FormattedMessage
                                id="api.apiSdks.labels.sdksTitle"
                                defaultMessage="Software Development Kits (SDKs)"
                            />
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} >
                        {loading ? (
                            <div className={classes.loaderContainer}>
                                <PreLoaderLogo size="medium" />
                            </div>
                        ) :
                            (
                                sdkLanguages.length > 0 ? (
                                    <Grid container justifyContent='flex-start' spacing={2} className={classes.sdkCardWrapper}>
                                        {sdkCards}
                                    </Grid>
                                ) : (
                                        <Grid container justifyContent='flex-start'>
                                            <Typography className={classes.notification}>
                                                <FormattedMessage
                                                    id="api.apiSdks.labels.noSdksAvailable"
                                                    defaultMessage="No SDKs available"
                                                />
                                            </Typography>
                                        </Grid>
                                    )
                            )}
                    </Grid>
                </Grid>
            </Container>
            <div className={classes.appBarSpacer} />
        </main>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiInfo: state.apiReducer,
        sdkInfo: state.sdkReducer,
        userReducer: state.userReducer
    }
}

export default connect(mapStateToProps)(ApiSdks);
